import React from 'react';
import { MetricWithTrend } from '../lib/types';
import { formatNumber, formatCurrency, formatPercentage, getTrendIndicator, getTrendColorClass } from '../lib/export';

interface MetricBoxProps {
  title: string;
  value: number | MetricWithTrend;
  format?: 'number' | 'currency' | 'percentage';
  decimals?: number;
  subtitle?: string;
  className?: string;
}

export function MetricBox({ 
  title, 
  value, 
  format = 'number', 
  decimals = 0,
  subtitle,
  className = ''
}: MetricBoxProps) {
  const metricValue = typeof value === 'number' ? value : value.value;
  const trend = typeof value === 'object' ? value.trend : undefined;
  
  let formattedValue: string;
  switch (format) {
    case 'currency':
      formattedValue = formatCurrency(metricValue);
      break;
    case 'percentage':
      formattedValue = formatPercentage(metricValue, decimals);
      break;
    default:
      formattedValue = formatNumber(metricValue, decimals);
  }

  const trendIndicator = getTrendIndicator(trend);
  const trendColor = getTrendColorClass(trend);

  return (
    <div className={`bg-white p-6 rounded-lg shadow ${className}`}>
      <h3 className="text-sm font-medium text-gray-500 mb-2">{title}</h3>
      <div className="flex items-baseline gap-2">
        <p className="text-3xl font-bold text-gray-900">{formattedValue}</p>
        {trend !== undefined && trend !== 0 && (
          <span className={`text-sm font-medium ${trendColor}`}>
            {trendIndicator} {Math.abs(trend).toFixed(1)}%
          </span>
        )}
      </div>
      {subtitle && <p className="text-xs text-gray-500 mt-1">{subtitle}</p>}
    </div>
  );
}

interface MachineStatusBoxProps {
  title: string;
  count: number;
  color: 'green' | 'red' | 'yellow' | 'blue';
  className?: string;
}

export function MachineStatusBox({ title, count, color, className = '' }: MachineStatusBoxProps) {
  const colorClasses = {
    green: 'text-green-600',
    red: 'text-red-600',
    yellow: 'text-yellow-600',
    blue: 'text-blue-600'
  };

  return (
    <div className={`bg-white p-6 rounded-lg shadow ${className}`}>
      <h3 className="text-sm font-medium text-gray-500 mb-2">{title}</h3>
      <p className={`text-3xl font-bold ${colorClasses[color]}`}>{count}</p>
    </div>
  );
}
